import { useEffect, useState } from "react";
import axios from "axios";
import { API, fmtErr } from "../lib/pos";
import { LayoutGrid, Plus, Trash2, GripVertical, Save } from "lucide-react";

/**
 * Online Categories admin — manager-only. CRUD for the tiles that appear on the
 * customer QR menu. Each tile carries a name + a set of existing kitchen
 * categories; the customer sees only tiles that resolve to at-least-one live
 * category with at-least-one active item.
 */
export default function OnlineCategoriesAdmin() {
  const [rows, setRows] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(null);

  const load = async () => {
    try {
      const [oc, cats] = await Promise.all([
        axios.get(`${API}/online-categories`),
        axios.get(`${API}/categories/manage`),
      ]);
      setRows(oc.data || []);
      setAllCats((cats.data || []).map(c => c.name).sort());
    } catch (e) { setErr(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/online-categories`, { name });
      setNewName("");
      await load();
    } catch (e) { setErr(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const toggleCat = async (oc, cat) => {
    const set = new Set(oc.categories || []);
    if (set.has(cat)) set.delete(cat); else set.add(cat);
    try {
      await axios.put(`${API}/online-categories/${oc.id}`, { categories: Array.from(set) });
      setSaved(oc.id);
      setTimeout(() => setSaved(null), 1500);
      await load();
    } catch (e) { setErr(fmtErr(e)); }
  };

  const rename = async (oc, newLabel) => {
    if (!newLabel.trim() || newLabel === oc.name) return;
    try {
      await axios.put(`${API}/online-categories/${oc.id}`, { name: newLabel.trim() });
      await load();
    } catch (e) { setErr(fmtErr(e)); }
  };

  const remove = async (oc) => {
    if (!window.confirm(`Delete online category "${oc.name}"?`)) return;
    try {
      await axios.delete(`${API}/online-categories/${oc.id}`);
      await load();
    } catch (e) { setErr(fmtErr(e)); }
  };

  const reorder = async (oc, direction) => {
    const idx = rows.findIndex(r => r.id === oc.id);
    const neighbourIdx = idx + direction;
    if (neighbourIdx < 0 || neighbourIdx >= rows.length) return;
    const a = rows[idx], b = rows[neighbourIdx];
    try {
      await Promise.all([
        axios.put(`${API}/online-categories/${a.id}`, { sort_order: b.sort_order }),
        axios.put(`${API}/online-categories/${b.id}`, { sort_order: a.sort_order }),
      ]);
      await load();
    } catch (e) { setErr(fmtErr(e)); }
  };

  return (
    <div className="settings-page" data-testid="online-cats-page">
      <div className="section-line" style={{ marginBottom: 6 }}>
        <h2><LayoutGrid size={18} style={{ verticalAlign: "-3px", marginRight: 8 }} />Online Categories</h2>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Create tiles that appear on the customer QR menu (e.g. <b>Breakfast</b>, <b>Mains</b>, <b>Desserts</b>).
        Each tile groups one or more of your existing kitchen categories. Customers tap a tile → sub-tiles by category → items sorted low → high price.
      </p>

      <div className="oc-new-row" style={{ display: "flex", gap: 8, marginTop: 18 }}>
        <input
          data-testid="oc-new-name"
          placeholder="New tile name — e.g. Breakfast"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          style={{ flex: 1 }}
        />
        <button data-testid="oc-create" className="primary" disabled={busy || !newName.trim()} onClick={create}>
          <Plus size={14} style={{ marginRight: 4, verticalAlign: -2 }} />Create tile
        </button>
      </div>
      {err && <p style={{ color: "var(--red)", marginTop: 8, fontSize: 12 }} data-testid="oc-error">{err}</p>}

      <div className="oc-list" style={{ display: "grid", gap: 12, marginTop: 22 }}>
        {rows.length === 0 && <p className="muted" style={{ fontStyle: "italic" }}>No tiles yet. Create your first one above.</p>}
        {rows.map((oc, i) => (
          <div key={oc.id} className="oc-card" data-testid={`oc-card-${oc.id}`}
            style={{ background: "var(--surface2)", border: "1px solid var(--line)", borderRadius: 6, padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <button className="outline" style={{ padding: "1px 4px", fontSize: 10 }} disabled={i === 0} onClick={() => reorder(oc, -1)}>▲</button>
                <button className="outline" style={{ padding: "1px 4px", fontSize: 10 }} disabled={i === rows.length - 1} onClick={() => reorder(oc, 1)}>▼</button>
              </div>
              <input
                data-testid={`oc-name-${oc.id}`}
                defaultValue={oc.name}
                onBlur={(e) => rename(oc, e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                style={{ flex: 1, fontSize: 18, fontWeight: 700 }}
              />
              {saved === oc.id && <span style={{ color: "var(--green)", fontSize: 11 }}>✓ Saved</span>}
              <button data-testid={`oc-delete-${oc.id}`} className="outline" onClick={() => remove(oc)} title="Delete tile">
                <Trash2 size={14} />
              </button>
            </div>
            <p className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5, margin: "0 0 6px" }}>Assigned kitchen categories</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {allCats.map((cat) => {
                const on = (oc.categories || []).includes(cat);
                return (
                  <button
                    key={cat}
                    type="button"
                    data-testid={`oc-cat-${oc.id}-${cat}`}
                    onClick={() => toggleCat(oc, cat)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 3,
                      fontSize: 12,
                      fontWeight: 600,
                      border: `1px solid ${on ? "#c2185b" : "var(--line)"}`,
                      background: on ? "#c2185b" : "transparent",
                      color: on ? "#fff" : "var(--muted)",
                      cursor: "pointer",
                    }}
                  >{on ? "✓ " : ""}{cat}</button>
                );
              })}
              {allCats.length === 0 && <span className="muted" style={{ fontSize: 12 }}>No kitchen categories exist yet — create some in Menu first.</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
